import { useNavigate } from "react-router";
import { TableToolbar } from "@rio-cloud/rio-uikit";
import { useIntl } from "react-intl";
import { fixedColumns, scrollableColumns } from "./tableConfigs";
import { renderTableCaption, renderTableHead } from "../../../../utils/tableUtils";
import { getTableLabels } from "./tableLabels";

import "../styles.scss";
import { useMediaQuery } from "@mui/material";

const SkeletonTable = (props: { onlyRows?: boolean | undefined }) => {
  const navigate = useNavigate();
  const isResponsiveView = useMediaQuery("(max-width: 1024px)");
  const intl = useIntl();
  const tableLabels = getTableLabels(intl);

  // Calcular largura mínima total para view responsivo
  const totalMinWidth = [...fixedColumns, ...scrollableColumns].reduce((acc, column) => {
    const widthToUse = column.details?.width && column.details.width > 0 
      ? column.details.width 
      : column.details?.defaultWidth || 100;
    return acc + widthToUse;
  }, 0);

  // Renderização para telas pequenas (mobile/tablet)
  if (isResponsiveView) {
    return (
      <div id="TableCommonDemo">
        {!props.onlyRows && (
          <TableToolbar>
            <div className="table-toolbar-container">
              <div className="table-toolbar-group-right">
                <div className="table-toolbar-column table-toolbar-column-spacer">
                  <button
                    type="button"
                    className="btn bg-highlight-darker padding-7"
                    onClick={() => navigate(`/control-tower/appointments/appointment`)}
                  >
                    <span className="text-capitalize padding-right-5">
                      Criar agendamento
                    </span>
                    <span
                      className="rioglyph rioglyph-plus-sign"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </div>
            </div>
          </TableToolbar>
        )}

        <div className="control-tower-responsive-table tables-row" style={{ position: 'relative' }}>
          <div className="overflow-x-auto">
            <table 
              className="table table-head-filled table-bordered min-w-full"
              style={{ 
                tableLayout: "fixed",
                width: "auto",
                minWidth: `${totalMinWidth}px`
              }}
            >
              <colgroup>
                {fixedColumns.map((column) =>
                  renderTableCaption(column.value, column.details)
                )}
                {scrollableColumns.map((column) =>
                  renderTableCaption(column.value, column.details)
                )}
              </colgroup>
              {!props.onlyRows && (
                <thead>
                  <tr>
                    {fixedColumns.map((column) => renderTableHead(
                      column.value,
                      column.label,
                      "vehicle",
                      () => { }
                    ))}
                    {scrollableColumns.map((column) => renderTableHead(
                      column.value,
                      column.label,
                      "vehicle",
                      () => { }
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {new Array(50).fill(0).map((row: any, rowIndex: number) => (
                  <tr key={rowIndex}>
                    {[...fixedColumns, ...scrollableColumns].map((column, colIndex) => (
                      <td
                        key={`${column.value}-${rowIndex}-${colIndex}`}
                        data-field={column.label}
                        className="animate-pulse"
                      >
                        <div className="w-3/4 h-4 bg-gray-300 rounded" />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // Renderização para desktop (acima de 1024px)
  return (
    <div id="TableCommonDemo">
      {!props.onlyRows && (
        <TableToolbar>
          <div className="table-toolbar-container">
            <div className="table-toolbar-group-right">
              <div className="table-toolbar-column table-toolbar-column-spacer">
                <button
                  type="button"
                  className="btn bg-highlight-darker padding-7"
                  onClick={() => navigate(`/control-tower/appointments/appointment`)}
                >
                  <span className="text-capitalize padding-right-5">
                    {intl.formatMessage({ id: "intl-msg:monitoring.createScheduling" })}
                  </span>
                  <span
                    className="rioglyph rioglyph-plus-sign"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
          </div>
        </TableToolbar>
      )}

      <div className="tables-row" style={{ gap: 0, justifyContent: 'flex-start' }}>
        <div className="skeleton-tables-row__fixed min-w-[800px]">
          <table
            className="table table-layout-fixed table-bordered"
          >
            <colgroup>{fixedColumns.map((column) => renderTableCaption(column.value, column.details))}</colgroup>
            {!props.onlyRows && (
              <thead>
                <tr>{fixedColumns.map((column) => renderTableHead(column.value, tableLabels[column.value as keyof typeof tableLabels], "vehicle", () => {}))}</tr>
              </thead>
            )}
            <tbody>
              {new Array(50).fill(0).map((row: any, rowIndex: number) => (
                <tr key={rowIndex}>
                  {fixedColumns.map((column, colIndex) => (
                    <td
                      key={`${column}-${rowIndex}-${colIndex}`}
                      data-field={column.label}
                      style={column.value !== "veiculo" ? {whiteSpace: 'nowrap'} : {}}
                      className="animate-pulse"
                    >
                      <div className="w-3/4 h-4 bg-gray-300 rounded" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="skeleton-scrollabe-table">
          <table className="table table-layout-fixed table-column-overflow-hidden table-bordered table-head-filled">
            <colgroup>
              {scrollableColumns.map((column) => renderTableCaption(column.value, column.details))}
            </colgroup>
            {!props.onlyRows && (
              <thead>
                <tr>{scrollableColumns.map((column) => renderTableHead(column.value, tableLabels[column.value as keyof typeof tableLabels], "vehicle", () => {}))}</tr>
              </thead>
            )}
            <tbody>
              {new Array(50).fill(0).map((row: any, rowIndex: number) => (
                <tr key={rowIndex}>
                  {scrollableColumns.map((colum) => (
                    <td key={colum.value} className="animate-pulse">
                      <div className="w-3/4 h-4 bg-gray-300 rounded" />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SkeletonTable;
